import React from 'react';
import {
  Bell, AlertTriangle, Target, CheckCircle, BarChart3, Calendar,
  Clock, TrendingUp, Zap, ArrowRight, Users, PenTool, GraduationCap
} from 'lucide-react';

/**
 * CollegeJourneyHub - Multiple Context Scenarios Mockup
 *
 * This file demonstrates how the same interface adapts to different contexts:
 * 1. Deadline Context - Urgent deadlines drive layout
 * 2. Phase Context - Discovery vs Development vs Application phases
 * 3. Capacity Context - Light vs Heavy workload weeks
 * 4. Progress Context - Behind vs On Track vs Ahead
 */

// Scenario 1: DEADLINE CONTEXT - Urgent deadlines this week
const DeadlineContextScenario = () => {
  const urgentDeadlines = [
    { id: '1', title: 'Stanford REA Deadline', college: 'Stanford University', daysLeft: 3, type: 'application' },
    { id: '2', title: 'Common App Essay Due', college: 'Multiple Schools', daysLeft: 5, type: 'essay' },
    { id: '3', title: 'Letter of Rec Deadline', college: 'Harvard', daysLeft: 7, type: 'recommendation' }
  ];

  return (
    <div className="space-y-8">
      {/* PRIMARY FOCUS: Deadline Management */}
      <div className="bg-coral-50 border-2 border-coral-400 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Bell className="w-6 h-6 text-coral-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-coral-900 mb-1">
                  Urgent Deadlines
                </h2>
                <p className="text-sm text-coral-900 opacity-80">
                  3 critical deadlines this week - let's prioritize
                </p>
              </div>
              <button className="px-4 py-2 bg-coral-600 hover:bg-coral-700 text-white rounded-lg text-sm font-medium">
                Manage Deadlines
              </button>
            </div>

            <div className="space-y-3">
              {urgentDeadlines.map((deadline) => (
                <div key={deadline.id} className="bg-white bg-opacity-80 rounded-lg p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    deadline.daysLeft <= 3 ? 'bg-red-100 text-red-700' :
                    deadline.daysLeft <= 5 ? 'bg-amber-100 text-amber-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    <span className="font-bold text-lg">{deadline.daysLeft}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">{deadline.title}</h3>
                    <p className="text-sm text-slate-600">{deadline.college}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      deadline.type === 'application' ? 'bg-violet-100 text-violet-800' :
                      deadline.type === 'essay' ? 'bg-azure-100 text-azure-800' :
                      'bg-emerald-100 text-emerald-800'
                    }`}>
                      {deadline.type}
                    </span>
                    <button className="px-3 py-1 bg-coral-600 text-white rounded-lg text-xs font-medium">
                      Work On This
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* COLLEGE CARDS: Organized by urgency */}
      <div className="bg-white rounded-xl border border-slate-900 shadow-sm">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">Colleges by Priority</h2>
            <span className="text-sm text-slate-600">Organized by upcoming deadlines</span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Urgent colleges shown first with red borders */}
            <div className="aspect-[4/3] rounded-lg bg-white border-2 border-red-400 shadow-sm overflow-hidden">
              <div className="h-12 bg-gradient-to-r from-red-500 to-pink-500 relative">
                <div className="absolute top-2 right-2">
                  <span className="text-xs bg-white text-red-600 px-2 py-1 rounded-full font-bold">3 DAYS</span>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-bold text-slate-900 mb-1">Stanford University</h3>
                <p className="text-xs text-slate-600 mb-2">REA Deadline Critical</p>
                <div className="space-y-1">
                  <div className="text-xs text-red-600 font-medium">⚡ Action needed now</div>
                </div>
              </div>
            </div>
            {/* Regular colleges with standard styling */}
          </div>
        </div>
      </div>
    </div>
  );
};

// Scenario 2: PHASE CONTEXT - Discovery Phase (New student)
const DiscoveryPhaseScenario = () => {
  const discoveryTasks = [
    { id: '1', title: 'Complete Strategic Assessment', progress: 0, importance: 'critical' },
    { id: '2', title: 'Build Academic Profile', progress: 30, importance: 'high' },
    { id: '3', title: 'Define College Preferences', progress: 0, importance: 'high' },
    { id: '4', title: 'Research Career Interests', progress: 10, importance: 'medium' }
  ];

  return (
    <div className="space-y-8">
      {/* PRIMARY FOCUS: Strategic Foundation */}
      <div className="bg-teal-50 border-2 border-teal-400 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Target className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-teal-900 mb-1">
                  Strategic Foundation
                </h2>
                <p className="text-sm text-teal-900 opacity-80">
                  Complete your assessment to unlock personalized guidance
                </p>
              </div>
              <button className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium">
                Start Assessment
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {discoveryTasks.map((task) => (
                <div key={task.id} className="bg-white bg-opacity-80 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-900">{task.title}</h3>
                    <span className={`w-2 h-2 rounded-full ${
                      task.importance === 'critical' ? 'bg-red-500' :
                      task.importance === 'high' ? 'bg-amber-500' :
                      'bg-emerald-500'
                    }`}></span>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>Progress</span>
                      <span>{task.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-stone-200 rounded-full">
                      <div
                        className="h-full bg-teal-500 rounded-full transition-all duration-300"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  </div>
                  <button className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                    task.progress === 0
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-teal-100 text-teal-800 hover:bg-teal-200'
                  }`}>
                    {task.progress === 0 ? 'Get Started' : 'Continue'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* COLLEGE CARDS: Minimal with placeholders */}
      <div className="bg-white rounded-xl border border-slate-900 shadow-sm">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">College Exploration</h2>
            <span className="text-sm text-slate-600">Start building your list</span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['reach', 'target', 'safety'].map((category) => (
              <div key={category} className="text-center">
                <h3 className="font-semibold text-slate-900 mb-4 capitalize">{category} Schools</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <button
                      key={i}
                      className="aspect-square border-2 border-dashed border-stone-300 rounded-lg hover:border-stone-400 hover:bg-stone-50 flex items-center justify-center transition-colors"
                    >
                      <span className="text-stone-400 text-2xl">+</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-600 mt-2">Add your first {category} school</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Scenario 3: CAPACITY CONTEXT - Heavy workload week
const CapacityContextScenario = () => {
  const workloadData = {
    thisWeekHours: 35,
    tasksCount: 12,
    criticalTasks: 4,
    suggestions: [
      { id: '1', action: 'Reschedule 3 non-critical tasks', impact: 'Reduce week to 28h' },
      { id: '2', action: 'Batch similar tasks together', impact: 'Save 2-3 hours' },
      { id: '3', action: 'Focus on high-impact activities only', impact: 'Maintain quality' }
    ]
  };

  return (
    <div className="space-y-8">
      {/* PRIMARY FOCUS: Workload Management */}
      <div className="bg-indigo-50 border-2 border-indigo-400 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-indigo-900 mb-1">
                  Heavy Week Management
                </h2>
                <p className="text-sm text-indigo-900 opacity-80">
                  {workloadData.thisWeekHours}h planned - let's optimize your schedule
                </p>
              </div>
              <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">
                Optimize Schedule
              </button>
            </div>

            {/* Workload Visualization */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-indigo-900">Weekly Capacity</span>
                <span className="text-sm text-indigo-900">{workloadData.thisWeekHours}h / 25h recommended</span>
              </div>
              <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-red-500 rounded-full"
                  style={{ width: `${Math.min(100, (workloadData.thisWeekHours / 25) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-red-600 mt-1 font-medium">140% of recommended capacity</p>
            </div>

            {/* Smart Suggestions */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-indigo-900">Smart Optimizations</h3>
              {workloadData.suggestions.map((suggestion) => (
                <div key={suggestion.id} className="bg-white bg-opacity-80 rounded-lg p-3 flex items-start gap-3">
                  <Zap className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 mb-1">{suggestion.action}</p>
                    <p className="text-xs text-slate-600">{suggestion.impact}</p>
                  </div>
                  <button className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-lg text-xs font-medium hover:bg-indigo-200 transition-colors">
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* COLLEGE CARDS: Organized by priority for this week */}
      <div className="bg-white rounded-xl border border-slate-900 shadow-sm">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">This Week's Focus</h2>
            <span className="text-sm text-slate-600">Prioritized by deadlines</span>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <h3 className="font-semibold text-amber-900">Critical This Week</h3>
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">{workloadData.criticalTasks} tasks</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <h4 className="font-medium text-slate-900 text-sm">Stanford REA Application</h4>
                  <p className="text-xs text-slate-600 mt-1">Final review needed - 3h remaining</p>
                </div>
                <div className="p-3 bg-white rounded-lg border border-amber-200">
                  <h4 className="font-medium text-slate-900 text-sm">Common App Essays</h4>
                  <p className="text-xs text-slate-600 mt-1">2 essays to finalize - 4h remaining</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-azure-50 border border-azure-200 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 bg-azure-500 rounded-full"></div>
                <h3 className="font-semibold text-azure-900">Can Be Rescheduled</h3>
                <span className="text-xs bg-azure-100 text-azure-800 px-2 py-1 rounded-full">8 tasks</span>
              </div>
              <p className="text-sm text-azure-800">Move these to lighter weeks to balance your workload</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Scenario 4: PROGRESS CONTEXT - On Track Success
const ProgressContextScenario = () => {
  const progressMetrics = {
    overallProgress: 78,
    completedApplications: 3,
    totalApplications: 8,
    completedEssays: 5,
    totalEssays: 7,
    onTrackDeadlines: 12,
    upcomingMilestones: [
      { id: '1', title: 'Complete UC Applications', dueDate: '2 weeks', status: 'on_track' },
      { id: '2', title: 'Submit MIT Application', dueDate: '3 weeks', status: 'ahead' },
      { id: '3', title: 'Schedule Alumni Interviews', dueDate: '1 month', status: 'on_track' }
    ]
  };

  return (
    <div className="space-y-8">
      {/* PRIMARY FOCUS: Progress Celebration */}
      <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-emerald-900 mb-1">
                  Excellent Progress!
                </h2>
                <p className="text-sm text-emerald-900 opacity-80">
                  You're {progressMetrics.overallProgress}% complete and on track for all deadlines
                </p>
              </div>
              <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
                View Details
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-900 mb-1">
                  {progressMetrics.completedApplications}/{progressMetrics.totalApplications}
                </div>
                <p className="text-sm text-emerald-800">Applications</p>
                <div className="text-xs text-emerald-700 mt-1">3 submitted ✓</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-900 mb-1">
                  {progressMetrics.completedEssays}/{progressMetrics.totalEssays}
                </div>
                <p className="text-sm text-emerald-800">Essays Complete</p>
                <div className="text-xs text-emerald-700 mt-1">2 remaining</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-900 mb-1">
                  {progressMetrics.onTrackDeadlines}
                </div>
                <p className="text-sm text-emerald-800">On Track</p>
                <div className="text-xs text-emerald-700 mt-1">All deadlines</div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-emerald-900">Upcoming Milestones</h3>
              {progressMetrics.upcomingMilestones.map((milestone) => (
                <div key={milestone.id} className="bg-white bg-opacity-80 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900 text-sm">{milestone.title}</h4>
                    <p className="text-xs text-slate-600">Due in {milestone.dueDate}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    milestone.status === 'ahead' ? 'bg-emerald-100 text-emerald-800' :
                    milestone.status === 'on_track' ? 'bg-emerald-100 text-emerald-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {milestone.status === 'ahead' ? '✓ Ahead' :
                     milestone.status === 'on_track' ? 'On Track' : 'Behind'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* COLLEGE CARDS: Organized by completion status */}
      <div className="bg-white rounded-xl border border-slate-900 shadow-sm">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">Application Portfolio</h2>
            <span className="text-sm text-emerald-600 font-medium">
              {progressMetrics.overallProgress}% Complete
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Submitted ({progressMetrics.completedApplications})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="aspect-[4/3] rounded-lg bg-emerald-50 border-2 border-emerald-400 p-4 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-emerald-900">Cal Poly SLO</h4>
                    <p className="text-sm text-emerald-700">Submitted Nov 15</p>
                  </div>
                  <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full self-start">
                    ✓ Complete
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-azure-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                In Progress ({progressMetrics.totalApplications - progressMetrics.completedApplications})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="aspect-[4/3] rounded-lg bg-azure-50 border border-azure-200 p-4 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-azure-900">Stanford University</h4>
                    <p className="text-sm text-azure-700">Essays complete, reviewing</p>
                  </div>
                  <div className="space-y-1">
                    <div className="h-1 bg-azure-200 rounded-full">
                      <div className="h-full bg-azure-500 rounded-full" style={{width: '85%'}}></div>
                    </div>
                    <span className="text-xs text-azure-700">85% complete</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main demonstration component
const ContextScenariosDemo = () => {
  const [currentScenario, setCurrentScenario] = React.useState('deadline');

  const scenarios = {
    deadline: {
      name: 'Deadline Context',
      description: 'Urgent deadlines this week',
      component: DeadlineContextScenario
    },
    discovery: {
      name: 'Discovery Phase',
      description: 'New student starting journey',
      component: DiscoveryPhaseScenario
    },
    capacity: {
      name: 'Heavy Workload',
      description: 'Overloaded week needs optimization',
      component: CapacityContextScenario
    },
    progress: {
      name: 'On Track',
      description: 'Excellent progress celebration',
      component: ProgressContextScenario
    }
  };

  const CurrentComponent = scenarios[currentScenario].component;

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: 'Reddit Sans, system-ui, sans-serif' }}>
      {/* Scenario Selector */}
      <div className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Contextual Scenarios Demo</h1>
            <p className="text-stone-300">Same interface, different contexts</p>
          </div>

          <div className="flex gap-2">
            {Object.entries(scenarios).map(([key, scenario]) => (
              <button
                key={key}
                onClick={() => setCurrentScenario(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentScenario === key
                    ? 'bg-white text-slate-900'
                    : 'bg-slate-800 text-white hover:bg-slate-700'
                }`}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current Scenario */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {scenarios[currentScenario].name}
          </h2>
          <p className="text-slate-600">
            {scenarios[currentScenario].description}
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          <div className="xl:col-span-3">
            <CurrentComponent />
          </div>

          {/* Static sidebar for context */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-xl border border-stone-200 p-6 sticky top-28">
              <h3 className="font-bold text-slate-900 mb-4">Context Analysis</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Primary Focus:</span>
                  <p className="text-slate-600 mt-1">
                    {currentScenario === 'deadline' && 'Urgent deadline management'}
                    {currentScenario === 'discovery' && 'Strategic foundation building'}
                    {currentScenario === 'capacity' && 'Workload optimization'}
                    {currentScenario === 'progress' && 'Progress celebration & planning'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Layout Priority:</span>
                  <p className="text-slate-600 mt-1">
                    {currentScenario === 'deadline' && 'Deadlines → Colleges → Secondary'}
                    {currentScenario === 'discovery' && 'Assessment → Placeholders → Guidance'}
                    {currentScenario === 'capacity' && 'Optimization → Prioritization → Focus'}
                    {currentScenario === 'progress' && 'Celebration → Portfolio → Milestones'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Visual Cues:</span>
                  <p className="text-slate-600 mt-1">
                    {currentScenario === 'deadline' && 'Coral urgency colors, countdown timers'}
                    {currentScenario === 'discovery' && 'Teal guidance colors, progress bars'}
                    {currentScenario === 'capacity' && 'Indigo optimization, workload visualization'}
                    {currentScenario === 'progress' && 'Emerald success colors, completion status'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextScenariosDemo;